import { getWrestlerName, getWrestlerNameUpper } from './wrestlerUtils';
import React, { useState, useMemo, useEffect, useRef } from 'react';

const placeholder = process.env.PUBLIC_URL + '/wrestlers/placeholder.png';
const placeholderTitle = process.env.PUBLIC_URL + '/titles/placeholder.png';
const trophyAsset = process.env.PUBLIC_URL + '/hof/trophy.png';

// ── HOF Trophy Animations CSS ────────────────────────────────────────
const TROPHY_ANIMATIONS_CSS = `
@keyframes trophyPulse {
  0% { transform: scale(1); filter: drop-shadow(0 0 0 rgba(212,175,55,0)); }
  50% { transform: scale(1.15); filter: drop-shadow(0 0 24px rgba(212,175,55,0.9)); }
  100% { transform: scale(1); filter: drop-shadow(0 0 8px rgba(212,175,55,0.4)); }
}
@keyframes trophyManualGlow {
  0% { box-shadow: 0 0 0 0 rgba(212,175,55,0); }
  50% { box-shadow: 0 0 30px 8px rgba(212,175,55,0.5); }
  100% { box-shadow: 0 0 0 0 rgba(212,175,55,0); }
}
@keyframes overlayFadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes textSlideUp {
  from { transform: translateY(40px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}
@keyframes victoryShake {
  0%, 100% { transform: scale(1); }
  10%, 30%, 50%, 70% { transform: scale(1.05) rotate(-2deg); }
  20%, 40%, 60%, 80% { transform: scale(1.05) rotate(2deg); }
}
@keyframes pcSpotLight {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes pcTrophyRise {
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes pcFadeInUp {
  from { opacity: 0; transform: translateY(18px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes pcLineExpand {
  from { transform: scaleX(0); opacity: 0; }
  to { transform: scaleX(1); opacity: 1; }
}
@keyframes pcTrophyGlow {
  0%, 100% { filter: drop-shadow(0 0 18px rgba(212,175,55,0.5)); }
  50% { filter: drop-shadow(0 0 36px rgba(212,175,55,0.85)); }
}
.trophy-just-activated {
  animation: trophyManualGlow 0.8s ease-out;
}
.trophy-just-activated img {
  animation: trophyPulse 0.8s ease-out;
}
`;



// ── HOF Goal Reached — Press Conference ──────────────────────────────
const HOFGoalReachedAnimation = ({ goal, gmName, onComplete }) => {
  const [visible, setVisible] = React.useState(true);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(onComplete, 600);
    }, 7000);
    return () => clearTimeout(timer);
  }, [onComplete]);

  if (!visible) return null;

  var displayName = gmName ? gmName.toUpperCase() : 'GM';

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 9999, background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', animation: 'overlayFadeIn 0.6s ease-out' }}
      onClick={() => { setVisible(false); setTimeout(onComplete, 200); }}
    >
      {/* Spotlight beam from top */}
      <div style={{
        position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
        width: '340px', height: '100%',
        background: 'radial-gradient(ellipse 170px 60% at 50% 0%, rgba(212,175,55,0.18) 0%, rgba(212,175,55,0.06) 50%, transparent 80%)',
        animation: 'pcSpotLight 1.6s ease-out 0.2s both',
        pointerEvents: 'none',
      }} />

      {/* Content */}
      <div style={{ position: 'relative', zIndex: 2, textAlign: 'center', padding: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

        {/* Congratulations line */}
        <div style={{ fontFamily: "'Monda', sans-serif", fontSize: '0.5rem', letterSpacing: '4px', color: 'rgba(212,175,55,0.75)', marginBottom: '10px', animation: 'pcFadeInUp 0.7s ease-out 0.3s both' }}>
          CONGRATULATIONS,&nbsp;
          <span style={{ color: '#d4af37', fontWeight: 700 }}>{displayName}</span>
        </div>

        {/* Sub-congrats line */}
        <div style={{ fontFamily: "'Monda', sans-serif", fontSize: '0.45rem', letterSpacing: '3px', color: 'rgba(255,255,255,0.4)', marginBottom: '44px', animation: 'pcFadeInUp 0.7s ease-out 0.55s both' }}>
          YOU'VE REACHED YOUR HALL OF FAME GOAL
        </div>

        {/* Trophy */}
        <div style={{ marginBottom: '40px', animation: 'pcTrophyRise 1s cubic-bezier(0.22, 1, 0.36, 1) 0.8s both' }}>
          <img
            src={trophyAsset} alt=""
            style={{ width: '200px', height: '200px', objectFit: 'contain', animation: 'pcTrophyGlow 2.5s ease-in-out 1.8s infinite' }}
            onError={e => { e.target.style.display = 'none'; }}
          />
        </div>

        {/* Main title */}
        <div style={{ fontFamily: "'Anton', sans-serif", fontSize: '2.6rem', letterSpacing: '10px', color: '#fff', animation: 'pcFadeInUp 0.8s ease-out 1.6s both' }}>
          HALL OF FAME
        </div>

        {/* Separator line */}
        <div style={{ width: '120px', height: '1px', background: 'linear-gradient(90deg, transparent, #d4af37, transparent)', margin: '16px auto', animation: 'pcLineExpand 0.6s ease-out 2s both', transformOrigin: 'center' }} />

        {/* Goal achieved */}
        <div style={{ fontFamily: "'Monda', sans-serif", fontSize: '0.5rem', letterSpacing: '5px', color: '#d4af37', marginBottom: '6px', animation: 'pcFadeInUp 0.6s ease-out 2.2s both' }}>
          GOAL ACHIEVED
        </div>

        {/* Season complete */}
        <div style={{ fontFamily: "'Monda', sans-serif", fontSize: '0.45rem', letterSpacing: '4px', color: 'rgba(255,255,255,0.35)', animation: 'pcFadeInUp 0.6s ease-out 2.5s both' }}>
          SEASON COMPLETE
        </div>

        {/* Click to continue */}
        <div style={{ fontFamily: "'Monda', sans-serif", fontSize: '0.4rem', letterSpacing: '2px', color: 'rgba(255,255,255,0.2)', marginTop: '52px', animation: 'pcFadeInUp 0.6s ease-out 3.2s both' }}>
          CLICK ANYWHERE TO CONTINUE
        </div>
      </div>
    </div>
  );
};


const getWrestlerRender = (w) => {
  if (!w) return null;
  if (w.customRender) return w.customRender;
  if (w.wrestler_id) return process.env.PUBLIC_URL + '/wrestlers/' + w.wrestler_id + '-render.png';
  return null;
};

const getTitleName = (key, champions) => {
  if (!key) return null;
  var slots = (champions && champions.slots) || [];
  var slot = slots.find(function(s) { return s.key === key; });
  return (slot && slot.titleOverride && slot.titleOverride.name) || (slot && slot.label) || key;
};

const getTitleRender = (key, champions) => {
  var slots = (champions && champions.slots) || [];
  var slot = slots.find(function(s) { return s.key === key; });
  if (slot && slot.titleOverride && slot.titleOverride.render) return slot.titleOverride.render;
  if (slot && slot.titleOverride && slot.titleOverride.id) return process.env.PUBLIC_URL + '/titles/' + slot.titleOverride.id + '-render.png';
  return placeholderTitle;
};

const BRAND_COLORS_MAP = { 'Raw': '#c0392b', 'SmackDown': '#1a6bb5', 'NXT': '#6c3483' };

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Anton&family=Monda:wght@400;700&display=swap');
  * { box-sizing: border-box; }
  html { font-size: 133%; }
  .hof-card { transition: all 0.15s; cursor: pointer; }
  .hof-card:hover { transform: translateY(-2px); }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 2px; }
`;

const S = {
  anton: { fontFamily: "'Anton', sans-serif" },
  mono: { fontFamily: "'Monda', sans-serif" },
  panel: { background: 'rgba(8,8,8,0.88)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px' },
  label: { fontFamily: "'Monda',sans-serif", fontSize: '0.56rem', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.35)' },
};

// ── Compute career stats from seasonHistory ──────────────────────────
const computeStats = (seasonHistory, roster, titleHistory, champions) => {
  var matchCounts = {};
  var winCounts = {};
  var maleMost = null, femaleMost = null, mostWins = null;

  (seasonHistory || []).forEach(function(week) {
    (week.matches || []).forEach(function(m) {
      (m.participants || []).forEach(function(name) {
        matchCounts[name] = (matchCounts[name] || 0) + 1;
      });
      if (m.winner && !m.noContest) {
        winCounts[m.winner] = (winCounts[m.winner] || 0) + 1;
      }
    });
  });

  // Find male/female with most matches
  var rosterMap = {};
  (roster || []).forEach(function(w) { if (w) rosterMap[getWrestlerName(w, 'display')] = w; });

  Object.entries(matchCounts).forEach(function(entry) {
    var name = entry[0], count = entry[1];
    var w = rosterMap[name];
    if (!w) return;
    if (w.gender === 'M') {
      if (!maleMost || count > maleMost.count) maleMost = { wrestler: w, count };
    } else {
      if (!femaleMost || count > femaleMost.count) femaleMost = { wrestler: w, count };
    }
  });

  // Most wins
  Object.entries(winCounts).forEach(function(entry) {
    var name = entry[0], count = entry[1];
    var w = rosterMap[name];
    if (!w) return;
    if (!mostWins || count > mostWins.count) mostWins = { wrestler: w, count };
  });

  // Title history stats
  var longestReign = null;
  var mostDefended = null;
  var defenseCount = {};

  (titleHistory || []).forEach(function(entry) {
    if (!entry.titleKey) return;
    var duration = entry.endWeek ? entry.endWeek - entry.startWeek : 0;
    if (!longestReign || duration > longestReign.duration) {
      var w = (entry.champions || []).length > 0 ? rosterMap[entry.champions[0]] : null;
      longestReign = { name: (entry.champions || []).join(' & '), wrestler: w, duration, titleKey: entry.titleKey };
    }
    defenseCount[entry.titleKey] = (defenseCount[entry.titleKey] || 0) + 1;
  });

  Object.entries(defenseCount).forEach(function(entry) {
    var key = entry[0], count = entry[1];
    if (!mostDefended || count > mostDefended.count) {
      mostDefended = { titleKey: key, count };
    }
  });

  return { maleMost, femaleMost, mostWins, longestReign, mostDefended };
};

// ── Stat Card ────────────────────────────────────────────────────────
const StatCard = ({ label, wrestler, value, sub, titleKey, champions, brandColor }) => {
  var render = wrestler ? (getWrestlerRender(wrestler) || placeholder) : null;
  var titleRender = titleKey ? getTitleRender(titleKey, champions) : null;

  return (
    <div style={{ ...S.panel, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <div style={{ height: '2px', background: 'linear-gradient(90deg, transparent, ' + brandColor + ', transparent)' }} />
      <div style={{ padding: '10px 12px 6px', ...S.label }}>{label}</div>
      <div style={{ padding: '0 12px 12px', display: 'flex', alignItems: 'flex-end', gap: '10px', flex: 1 }}>
        {render && (
          <img src={render} alt="" style={{ width: '64px', height: '82px', objectFit: 'contain', objectPosition: 'top', filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.9))', flexShrink: 0 }} />
        )}
        {titleRender && !render && (
          <img src={titleRender} alt="" style={{ width: '64px', height: '64px', objectFit: 'contain', flexShrink: 0 }} onError={e => { e.target.style.display='none'; }} />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ ...S.anton, fontSize: '0.92rem', letterSpacing: '1px', color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {wrestler ? getWrestlerNameUpper(wrestler, 'display') : (sub || '—')}
          </div>
          <div style={{ ...S.anton, fontSize: '2rem', letterSpacing: '1px', color: brandColor }}>{value}</div>
          {sub && wrestler && <div style={{ ...S.mono, fontSize: '0.92rem', color: 'rgba(255,255,255,0.35)', marginTop: '2px' }}>{sub}</div>}
        </div>
      </div>
    </div>
  );
};

// ── HOF Trophy ───────────────────────────────────────────────────────
const HOFTrophy = ({ trophy, index, active, autoActive, onToggle, brandColor, progress, justActivated }) => {
  var isActive = active || autoActive;
  var borderColor = autoActive ? '#2ecc71' : active ? '#d4af37' : 'rgba(255,255,255,0.08)';
  var bg = autoActive ? 'rgba(46,204,113,0.07)' : active ? 'rgba(212,175,55,0.07)' : 'rgba(8,8,8,0.88)';

  return (
    <div className={"hof-card" + (justActivated ? " trophy-just-activated" : "")} onClick={() => !autoActive && onToggle(index)}
      style={{ borderRadius: '10px', border: '1px solid ' + borderColor, background: bg, padding: '14px 10px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
      <img src={trophyAsset} alt=""
        style={{ width: '64px', height: '64px', objectFit: 'contain', filter: isActive ? 'none' : 'grayscale(1)', opacity: isActive ? 1 : 0.3, transition: 'all 0.2s' }}
        onError={e => { e.target.style.display='none'; }} />
      <div style={{ ...S.mono, fontSize: '0.56rem', fontWeight: '700', color: autoActive ? '#2ecc71' : active ? '#d4af37' : 'rgba(255,255,255,0.35)', lineHeight: '1.3', letterSpacing: '0.5px' }}>
        {trophy.name}
      </div>
      {trophy.condition && (
        <div style={{ ...S.mono, fontSize: '0.60rem', color: autoActive ? '#2ecc71' : 'rgba(255,255,255,0.2)', letterSpacing: '0.5px' }}>
          {autoActive ? '✓ AUTO' : trophy.condition}
        </div>
      )}
      {!trophy.condition && !autoActive && (
        <div style={{ ...S.mono, fontSize: '0.60rem', color: active ? '#d4af37' : 'rgba(255,255,255,0.2)' }}>
          {active ? '✓ ACTIVE' : 'INACTIVE'}
        </div>
      )}
      {/* Progress bar for auto trophies */}
      {progress !== undefined && !autoActive && (
        <div style={{ width: '100%', marginTop: '2px' }}>
          <div style={{ height: '3px', background: 'rgba(255,255,255,0.08)', borderRadius: '2px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: Math.min(progress * 100, 100) + '%', background: progress >= 1 ? '#d4af37' : brandColor, borderRadius: '2px', transition: 'width 0.3s' }} />
          </div>
          <div style={{ ...S.mono, fontSize: '0.36rem', color: 'rgba(255,255,255,0.25)', marginTop: '2px', textAlign: 'center' }}>
            {Math.round(progress * 100)}%
          </div>
        </div>
      )}
    </div>
  );
};

// ── Load Chart.js ────────────────────────────────────────────────────
var chartJsLoaded = false;
var chartJsCallbacks = [];
function loadChartJs(cb) {
  if (window.Chart) { cb(); return; }
  chartJsCallbacks.push(cb);
  if (chartJsLoaded) return;
  chartJsLoaded = true;
  var s = document.createElement('script');
  s.src = 'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.js';
  s.onload = function() { chartJsCallbacks.forEach(function(fn) { fn(); }); chartJsCallbacks = []; };
  document.head.appendChild(s);
}

// ── Line Chart (fans) ─────────────────────────────────────────────────
const FansLineChart = ({ datasets, labels, formatY }) => {
  var canvasRef = useRef(null);
  var chartRef = useRef(null);

  useEffect(function() {
    loadChartJs(function() {
      if (!canvasRef.current) return;
      if (chartRef.current) { chartRef.current.destroy(); }
      chartRef.current = new window.Chart(canvasRef.current, {
        type: 'line',
        data: { labels, datasets },
        options: {
          responsive: true, maintainAspectRatio: false, animation: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: function(ctx) { return ctx.dataset.label + ': ' + (formatY ? formatY(ctx.parsed.y) : ctx.parsed.y); }
              },
              backgroundColor: 'rgba(0,0,0,0.85)', titleFont: { size: 10 }, bodyFont: { size: 10 },
            }
          },
          scales: {
            x: { ticks: { font: { size: 9 }, color: '#666', maxRotation: 0, autoSkip: labels.length > 10 }, grid: { color: 'rgba(255,255,255,0.04)' } },
            y: { ticks: { font: { size: 9 }, color: '#666', callback: function(v) { return formatY ? formatY(v) : v; } }, grid: { color: 'rgba(255,255,255,0.04)' } }
          }
        }
      });
    });
    return function() { if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; } };
  }, [datasets, labels]);

  return (
    <div style={{ position: 'relative', height: '200px' }}>
      <canvas ref={canvasRef} />
    </div>
  );
};

// ── Bar Chart (budget) ────────────────────────────────────────────────
const BudgetBarChart = ({ data, costData, labels, color, formatY }) => {
  var canvasRef = useRef(null);
  var chartRef = useRef(null);

  useEffect(function() {
    loadChartJs(function() {
      if (!canvasRef.current) return;
      if (chartRef.current) { chartRef.current.destroy(); }
      chartRef.current = new window.Chart(canvasRef.current, {
        type: 'bar',
        data: {
          labels,
          datasets: [
            {
              label: 'Budget',
              data,
              backgroundColor: data.map(function(v, i) {
                return i > 0 && data[i] < data[i-1] ? 'rgba(231,76,60,0.4)' : 'rgba(46,204,113,0.35)';
              }),
              borderColor: data.map(function(v, i) {
                return i > 0 && data[i] < data[i-1] ? '#e74c3c' : '#2ecc71';
              }),
              borderWidth: 1, borderRadius: 3, yAxisID: 'y',
            },
            ...(costData && costData.length > 0 ? [{
              label: 'Show Cost',
              data: costData,
              backgroundColor: 'rgba(52,152,219,0.18)',
              borderColor: 'rgba(52,152,219,0.6)',
              borderWidth: 1, borderRadius: 3, yAxisID: 'y',
            }] : [])
          ]
        },
        options: {
          responsive: true, maintainAspectRatio: false, animation: false,
          plugins: {
            legend: { display: costData && costData.length > 0, labels: { color: 'rgba(255,255,255,0.4)', font: { size: 9 }, boxWidth: 10, padding: 12 } },
            tooltip: {
              callbacks: {
                label: function(ctx) {
                  var val = ctx.parsed.y;
                  if (ctx.dataset.label === 'Show Cost') return 'Show Cost: ' + (formatY ? formatY(val) : val);
                  var prev = ctx.dataIndex > 0 ? ctx.dataset.data[ctx.dataIndex - 1] : val;
                  var diff = val - prev;
                  var sign = diff >= 0 ? '+' : '';
                  return 'Budget: ' + (formatY ? formatY(val) : val) + (ctx.dataIndex > 0 ? '  (' + sign + (formatY ? formatY(diff) : diff) + ')' : '');
                }
              },
              backgroundColor: 'rgba(0,0,0,0.85)', titleFont: { size: 10 }, bodyFont: { size: 10 },
            }
          },
          scales: {
            x: { ticks: { font: { size: 9 }, color: '#666', maxRotation: 0, autoSkip: labels.length > 10 }, grid: { display: false } },
            y: { ticks: { font: { size: 9 }, color: '#666', callback: function(v) { return formatY ? formatY(v) : v; } }, grid: { color: 'rgba(255,255,255,0.04)' } }
          }
        }
      });
    });
    return function() { if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; } };
  }, [data, costData, labels]);

  return (
    <div style={{ position: 'relative', height: '200px' }}>
      <canvas ref={canvasRef} />
    </div>
  );
};

// ── Main CareerTab ───────────────────────────────────────────────────
export default function CareerTab({ gameData, onUpdateGameData }) {
  // Animation state
  const [hofGoalReached, setHofGoalReached] = React.useState(false);
  const [justActivatedIndex, setJustActivatedIndex] = React.useState(null);
  const hofGoalReachedRef = React.useRef(false);
  
  var brand = (gameData && gameData.brand) || '';
  var brandColor = (gameData && gameData.brandColor) || BRAND_COLORS_MAP[brand] || '#d4af37';
  var brandBg = (gameData && gameData.brandBg) || null;
  var fans = (gameData && gameData.fans) || 0;
  var budget = (gameData && gameData.budget) || 0;
  var week = (gameData && gameData.week) || 1;
  var season = (gameData && gameData.season) || 1;
  var roster = (gameData && gameData.roster) || [];
  var rivals = ((gameData && gameData.rivals) || []).filter(Boolean);
  var seasonHistory = (gameData && gameData.seasonHistory) || [];
  var titleHistory = (gameData && gameData.titleHistory) || [];
  var champions = (gameData && gameData.champions) || {};
  var hofGoal = (gameData && (gameData.hofTrophies !== undefined ? gameData.hofTrophies : (gameData.gameSettingsData && gameData.gameSettingsData.hofTrophies))) || 10;
  var hofActive = (gameData && gameData.hofActive) || [];
  var victoryCriteria = (gameData && gameData.gameSettingsData && gameData.gameSettingsData.victoryCriteria) || 'fans';

  // Auto trophies — fixed conditions
  var AUTO_FAN_TROPHIES = [
    { name: '2.3M Fans', condition: '2 300 000 fans', fanThreshold: 2300000 },
    { name: '3.4M Fans', condition: '3 400 000 fans', fanThreshold: 3400000 },
    { name: '4.5M Fans', condition: '4 500 000 fans', fanThreshold: 4500000 },
    { name: '5.25M Fans', condition: '5 250 000 fans', fanThreshold: 5250000 },
    { name: '6M Fans', condition: '6 000 000 fans', fanThreshold: 6000000 },
  ];

  // Cumulative profits from seasonHistory
  var totalProfits = (seasonHistory || []).reduce(function(sum, w) {
    return sum + (w.profits || 0);
  }, 0);

  var AUTO_PROFIT_TROPHIES = [
    { name: '2M Profits', condition: '$2 000 000', profitThreshold: 2000000 },
    { name: '5.25M Profits', condition: '$5 250 000', profitThreshold: 5250000 },
    { name: '8.75M Profits', condition: '$8 750 000', profitThreshold: 8750000 },
    { name: '12.5M Profits', condition: '$12 500 000', profitThreshold: 12500000 },
    { name: '16M Profits', condition: '$16 000 000', profitThreshold: 16000000 },
  ];

  // All trophy slots = hofGoal (auto unlocked ones glow green, still manually toggleable)

  // Ranking — include trophies for rivals
  var autoFanCount = AUTO_FAN_TROPHIES.filter(function(t) { return fans >= t.fanThreshold; }).length;
  var autoProfitCount = AUTO_PROFIT_TROPHIES.filter(function(t) { return totalProfits >= t.profitThreshold; }).length;
  var autoTotal = autoFanCount + autoProfitCount;
  // Auto trophies occupy slots — manual slots fill the remainder up to hofGoal
  // Effective manual = slots not already covered by auto trophies
  var manualSlotsTaken = hofActive.filter(function(idx) { return idx >= autoTotal; }).length;
  var myTrophies = Math.min(autoTotal + manualSlotsTaken, hofGoal);
  var rankingEntries = [{ name: brand, fans: fans, trophies: myTrophies, isMe: true }].concat(rivals.map(function(r) {
    var rFans = r.fans || 0;
    var rProfits = r.totalProfits || 0;
    var rHofActive = r.hofActive || [];
    var rAutoFan = AUTO_FAN_TROPHIES.filter(function(t) { return rFans >= t.fanThreshold; }).length;
    var rAutoProfit = AUTO_PROFIT_TROPHIES.filter(function(t) { return rProfits >= t.profitThreshold; }).length;
    var rAutoTotal = rAutoFan + rAutoProfit;
    var rManual = rHofActive.filter(function(idx) { return idx >= rAutoTotal; }).length;
    return { name: r.brand, fans: rFans, trophies: Math.min(rAutoTotal + rManual, hofGoal), isMe: false };
  }));
  rankingEntries.sort(function(a, b) {
    var primary = victoryCriteria === 'fans' ? (b.fans - a.fans) : (b.trophies - a.trophies);
    if (primary !== 0) return primary;
    return victoryCriteria === 'fans' ? (b.trophies - a.trophies) : (b.fans - a.fans);
  });
  var myRank = rankingEntries.findIndex(function(e) { return e.isMe; }) + 1;
  var primaryVal = victoryCriteria === 'fans' ? fans : myTrophies;
  var myPrimaryEntry = rankingEntries[myRank - 1];
  var prevEntry = myRank > 1 ? rankingEntries[myRank - 2] : null;
  var prevPrimary = prevEntry ? (victoryCriteria === 'fans' ? prevEntry.fans : prevEntry.trophies) : 0;
  var nextEntry = rankingEntries.length > 1 ? rankingEntries[1] : null;
  var nextPrimary = nextEntry ? (victoryCriteria === 'fans' ? nextEntry.fans : nextEntry.trophies) : 0;
  var fansGap = myRank === 1 ? (nextPrimary > 0 ? primaryVal - nextPrimary : 0) : prevPrimary - primaryVal;

  // Stats
  var stats = useMemo(function() {
    return computeStats(seasonHistory, roster, titleHistory, champions);
  }, [seasonHistory, roster, titleHistory, champions]);

  // Chart data — by PLE cycle
  var [selectedCycle, setSelectedCycle] = useState(0); // 0 = all

  var cycleCount = Math.ceil(50 / 5);
  var chartWeeks = selectedCycle === 0
    ? seasonHistory
    : seasonHistory.filter(function(w) {
        var cycle = Math.ceil(w.week / 5);
        return cycle === selectedCycle;
      });

  var fansData = chartWeeks.map(function(w) {
    var isPLE = w.weekType === 'ple' || w.weekType === 'wrestlemania';
    return { label: isPLE ? 'PLE S' + w.week : 'S' + w.week, value: w.fans || 0, diff: null };
  }).map(function(d, i, arr) {
    if (i === 0) return d;
    return { ...d, diff: d.value - arr[i-1].value };
  });

  var budgetData = chartWeeks.map(function(w) {
    var isPLE = w.weekType === 'ple' || w.weekType === 'wrestlemania';
    return { label: isPLE ? 'PLE S' + w.week : 'S' + w.week, value: w.budget || 0 };
  });

  var showCostData = chartWeeks.map(function(w) {
    return w.showCost || 0;
  });

  var rivalFansData = rivals.map(function(r) {
    var rivalHistory = chartWeeks.map(function(w) {
      // Use recorded rival fans per week if available, fallback to current fans
      var weekFans = (w.rivalFans && w.rivalFans[r.brand]) || null;
      return { label: 'S' + w.week, value: weekFans !== null ? weekFans : (r.fans || 0) };
    });
    return {
      brand: r.brand,
      color: BRAND_COLORS_MAP[r.brand] || '#888',
      data: rivalHistory,
    };
  });

  // Toggle HOF
  var handleToggle = function(index) {
    var current = hofActive.slice();
    var pos = current.indexOf(index);
    var isActivating = pos === -1;
    if (isActivating) current.push(index);
    else current.splice(pos, 1);
    
    // Trigger subtle animation when activating manually
    if (isActivating) {
      setJustActivatedIndex(index);
      setTimeout(() => setJustActivatedIndex(null), 800);
    }
    
    if (onUpdateGameData) onUpdateGameData({ ...gameData, hofActive: current });
  };

  var formatFans = function(v) {
    return Math.round(Number(v)).toLocaleString('fr-FR').replace(/,/g, ' ');
  };

  var formatBudget = function(v) {
    return '$' + Math.round(Number(v)).toLocaleString('fr-FR').replace(/,/g, ' ');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', height: '100%', overflowY: 'auto' }}>
      <style>{css}</style>
      <style>{TROPHY_ANIMATIONS_CSS}</style>
      
      {/* HOF goal reached - press conference animation */}
      {hofGoalReached && (
        <HOFGoalReachedAnimation
          goal={hofGoal}
          gmName={(gameData && gameData.gm) || ''}
          onComplete={() => setHofGoalReached(false)}
        />
      )}

      {/* ── Header stats ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px', flexShrink: 0 }}>
        {[
          { label: 'HOF TROPHIES', value: myTrophies + ' / ' + hofGoal, color: '#d4af37' },
          { label: 'FANS', value: formatFans(fans), color: '#ffffff' },
          { label: 'BUDGET', value: formatBudget(budget), color: '#ffffff' },
          { label: 'WEEK', value: week + ' / 50', color: '#ffffff' },
        ].map(function(s) {
          return (
            <div key={s.label} style={{ ...S.panel, padding: '16px 18px' }}>
              <div style={{ ...S.label, marginBottom: '5px' }}>{s.label}</div>
              <div style={{ ...S.anton, fontSize: '2rem', letterSpacing: '1px', color: s.color }}>{s.value}</div>
            </div>
          );
        })}
        {/* Classement */}
        <div style={{ ...S.panel, padding: '16px 18px', border: '1px solid ' + brandColor + '44', position: 'relative', overflow: 'hidden' }}>
          {brandBg && <img src={brandBg} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'blur(4px) brightness(0.2)', zIndex: 0 }} />}
          <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(ellipse at bottom, ${brandColor}22 0%, transparent 70%)`, zIndex: 1 }} />
          <div style={{ position: 'relative', zIndex: 2 }}>
          <div style={{ ...S.label, marginBottom: '5px' }}>STANDINGS</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '5px' }}>
            <div style={{ ...S.anton, fontSize: '2rem', color: brandColor }}>{myRank}</div>
            <div style={{ ...S.mono, fontSize: '0.56rem', color: 'rgba(255,255,255,0.35)' }}>/ {rankingEntries.length}</div>
          </div>
          <div style={{ ...S.mono, fontSize: '0.92rem', color: myRank === 1 ? '#2ecc71' : '#e74c3c', marginTop: '3px' }}>
            {myRank === 1
              ? (fansGap > 0 ? '+' + formatFans(fansGap) + ' vs #2' : 'Leading')
              : '-' + formatFans(Math.abs(fansGap)) + ' vs #' + (myRank - 1)}
          </div>
          </div>
        </div>
      </div>

      {/* ── HOF ── */}
      <div style={{ ...S.panel, overflow: 'hidden', flexShrink: 0, border: '1px solid rgba(212,175,55,0.25)', boxShadow: '0 0 24px rgba(212,175,55,0.12), inset 0 0 40px rgba(212,175,55,0.04)', position: 'relative' }}>
        <img src={process.env.PUBLIC_URL + '/hof/hof_background.png'} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.22, zIndex: 0, pointerEvents: 'none' }} onError={e => { e.target.style.display='none'; }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ ...S.anton, fontSize: '1.15rem', letterSpacing: '2px', color: '#d4af37' }}>HALL OF FAME</div>
          {(() => {
            var autoFan = AUTO_FAN_TROPHIES.filter(function(t) { return fans >= t.fanThreshold; }).length;
            var autoProfit = AUTO_PROFIT_TROPHIES.filter(function(t) { return totalProfits >= t.profitThreshold; }).length;
            var autoFanH = AUTO_FAN_TROPHIES.filter(function(t) { return fans >= t.fanThreshold; }).length;
            var autoProfitH = AUTO_PROFIT_TROPHIES.filter(function(t) { return totalProfits >= t.profitThreshold; }).length;
            var autoTotalH = autoFanH + autoProfitH;
            var manualH = hofActive.filter(function(idx) { return idx >= autoTotalH; }).length;
            var total = Math.min(autoTotalH + manualH, hofGoal);
            return (
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <div style={{ ...S.mono, fontSize: '0.54rem', color: total >= hofGoal ? '#d4af37' : 'rgba(255,255,255,0.35)' }}>
                  {total} / {hofGoal} {total >= hofGoal ? '🏆' : ''}
                </div>
              </div>
            );
          })()}
        </div>
        {/* Fans trophies */}
        <div style={{ padding: '10px 20px 4px', ...S.label }}>FANS</div>
        <div style={{ padding: '8px 20px 16px', display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          {AUTO_FAN_TROPHIES.map(function(trophy, i) {
            var isAutoActive = fans >= trophy.fanThreshold;
            return (
              <HOFTrophy key={'fan-' + i} trophy={trophy} index={-1} active={false} autoActive={isAutoActive} onToggle={function(){}} brandColor={brandColor} progress={isAutoActive ? 1 : Math.min(fans / trophy.fanThreshold, 1)} />
            );
          })}
        </div>

        {/* Profits trophies */}
        <div style={{ padding: '10px 20px 4px', ...S.label }}>PROFITS</div>
        <div style={{ padding: '8px 20px 16px', display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          {AUTO_PROFIT_TROPHIES.map(function(trophy, i) {
            var isAutoActive = totalProfits >= trophy.profitThreshold;
            return (
              <HOFTrophy key={'profit-' + i} trophy={trophy} index={-1} active={false} autoActive={isAutoActive} onToggle={function(){}} brandColor={brandColor} progress={isAutoActive ? 1 : Math.min(totalProfits / trophy.profitThreshold, 1)} />
            );
          })}
        </div>

        {/* All trophy slots — auto unlocked ones glow, all are manually toggleable */}
        <div style={{ padding: '10px 16px 4px', ...S.label }}>TROPHIES ({hofGoal} TOTAL)</div>
        <div style={{ padding: '8px 20px 18px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: '8px' }}>
          {Array.from({ length: hofGoal }, function(_, i) {
            // Auto trophies occupy the FIRST slots; manual trophies fill the rest
            var isAutoSlot = i < autoTotal;
            // For manual slots (i >= autoTotal), check if manually activated
            var isManualActive = !isAutoSlot && hofActive.includes(i);
            return (
              <HOFTrophy key={'slot-' + i} trophy={{ name: 'Trophy ' + (i + 1), condition: null }} index={i} active={isManualActive || isAutoSlot} autoActive={isAutoSlot} onToggle={handleToggle} brandColor={brandColor} justActivated={justActivatedIndex === i} />
            );
          })}
        </div>
        </div>
      </div>

      {/* ── Records & Stats ── */}
      <div style={{ flexShrink: 0 }}>
        <div style={{ ...S.label, marginBottom: '8px' }}>RECORDS & STATS</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px' }}>
          <StatCard label="TOP MALE MATCHES" wrestler={stats.maleMost && stats.maleMost.wrestler} value={stats.maleMost ? stats.maleMost.count + ' MATCHES' : '—'} brandColor={brandColor} />
          <StatCard label="TOP FEMALE MATCHES" wrestler={stats.femaleMost && stats.femaleMost.wrestler} value={stats.femaleMost ? stats.femaleMost.count + ' MATCHES' : '—'} brandColor={brandColor} />
          <StatCard label="MOST WINS" wrestler={stats.mostWins && stats.mostWins.wrestler} value={stats.mostWins ? stats.mostWins.count + ' WINS' : '—'} brandColor={brandColor} />
          <StatCard label="LONGEST REIGN" wrestler={stats.longestReign && stats.longestReign.wrestler} value={stats.longestReign ? stats.longestReign.duration + ' WEEKS' : '—'} sub={stats.longestReign ? getTitleName(stats.longestReign.titleKey, champions) : null} titleKey={stats.longestReign && stats.longestReign.titleKey} champions={champions} brandColor={brandColor} />
          <StatCard label="MOST DEFENDED" value={stats.mostDefended ? stats.mostDefended.count + ' DEFENSES' : '—'} sub={stats.mostDefended ? getTitleName(stats.mostDefended.titleKey, champions) : null} titleKey={stats.mostDefended && stats.mostDefended.titleKey} champions={champions} brandColor={brandColor} />
        </div>
      </div>

      {/* ── Charts ── */}
      <div style={{ flexShrink: 0 }}>
        {/* Cycle selector */}
        <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ ...S.label, marginRight: '6px' }}>CYCLE:</div>
          <button onClick={() => setSelectedCycle(0)}
            style={{ ...S.mono, fontSize: '0.54rem', background: selectedCycle === 0 ? brandColor + '22' : 'transparent', border: '1px solid ' + (selectedCycle === 0 ? brandColor : 'rgba(255,255,255,0.15)'), color: selectedCycle === 0 ? brandColor : 'rgba(255,255,255,0.4)', padding: '3px 10px', borderRadius: '3px', cursor: 'pointer' }}>
            SEASON
          </button>
          {[1,2,3,4,5,6,7,8,9,10].map(function(c) {
            var hasData = seasonHistory.some(function(w) { return Math.ceil(w.week / 5) === c; });
            if (!hasData) return null;
            return (
              <button key={c} onClick={() => setSelectedCycle(c)}
                style={{ ...S.mono, fontSize: '0.54rem', background: selectedCycle === c ? brandColor + '22' : 'transparent', border: '1px solid ' + (selectedCycle === c ? brandColor : 'rgba(255,255,255,0.15)'), color: selectedCycle === c ? brandColor : 'rgba(255,255,255,0.4)', padding: '3px 10px', borderRadius: '3px', cursor: 'pointer' }}>
                C{c}
              </button>
            );
          })}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
          {/* Fans chart */}
          <div style={{ ...S.panel, overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ ...S.anton, fontSize: '0.92rem', letterSpacing: '1.5px', color: '#3498db' }}>FANS</div>
              <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px', ...S.mono, fontSize: '0.60rem', color: 'rgba(255,255,255,0.4)' }}>
                  <span style={{ width: '12px', height: '2px', background: brandColor, display: 'inline-block' }} /> You
                </span>
                {rivals.map(function(r) { return (
                  <span key={r.brand} style={{ display: 'flex', alignItems: 'center', gap: '4px', ...S.mono, fontSize: '0.60rem', color: 'rgba(255,255,255,0.4)' }}>
                    <span style={{ width: '12px', height: '2px', background: BRAND_COLORS_MAP[r.brand] || '#888', display: 'inline-block' }} /> {r.brand}
                  </span>
                ); })}
              </div>
            </div>
            <div style={{ padding: '16px 18px' }}>
              {fansData.length > 0
                ? <>
                    <FansLineChart
                      labels={fansData.map(function(d) { return d.label; })}
                      formatY={formatFans}
                      datasets={[
                        { label: 'You', data: fansData.map(function(d) { return d.value; }), borderColor: brandColor, backgroundColor: brandColor + '18', tension: 0.4, pointRadius: function(ctx) { var l = fansData[ctx.dataIndex]; return l && l.label && l.label.startsWith('PLE') ? 4 : 2; }, borderWidth: 2, fill: true },
                      ].concat(rivalFansData.map(function(rd) { return { label: rd.brand, data: rd.data.map(function(d) { return d.value; }), borderColor: rd.color, backgroundColor: 'transparent', tension: 0.4, pointRadius: 2, borderWidth: 1.5 }; }))}
                    />
                    {/* Week-over-week diff row */}
                    {fansData.length > 1 && (
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '10px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                        {fansData.map(function(d, i) {
                          if (i === 0 || d.diff === null) return null;
                          var color = d.diff >= 0 ? '#2ecc71' : '#e74c3c';
                          return (
                            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px', minWidth: '36px' }}>
                              <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.36rem', color: 'rgba(255,255,255,0.25)' }}>{d.label}</span>
                              <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', color }}>{d.diff >= 0 ? '+' : ''}{(d.diff / 1000).toFixed(0)}K</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                : <div style={{ ...S.mono, fontSize: '1.05rem', color: 'rgba(255,255,255,0.2)', textAlign: 'center', padding: '30px 0' }}>No data yet</div>
              }
            </div>
          </div>

          {/* Budget chart */}
          <div style={{ ...S.panel, overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
              <div style={{ ...S.anton, fontSize: '0.92rem', letterSpacing: '1.5px', color: '#2ecc71' }}>BUDGET</div>
            </div>
            <div style={{ padding: '16px 18px' }}>
              {budgetData.length > 0
                ? <BudgetBarChart
                    labels={budgetData.map(function(d) { return d.label; })}
                    data={budgetData.map(function(d) { return d.value; })}
                    costData={showCostData}
                    color="#2ecc71"
                    formatY={formatBudget}
                  />
                : <div style={{ ...S.mono, fontSize: '1.05rem', color: 'rgba(255,255,255,0.2)', textAlign: 'center', padding: '30px 0' }}>No data yet</div>
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
